import { useState, useEffect, useCallback } from "react";
import { fetchOrgMembers, inviteMember, updateMemberRole, removeMember, fetchOrganizations } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { getErrorMessage } from "@/lib/utils";
import { UserPlus, Crown, ShieldCheck, User, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_ICONS = { OWNER: Crown, MANAGER: ShieldCheck, STAFF: User };
const ROLE_COLORS = { OWNER: "text-amber-600 bg-amber-50", MANAGER: "text-foreground bg-foreground/5", STAFF: "text-slate-600 bg-slate-50" };

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "STAFF" });
  const [inviteLoading, setInviteLoading] = useState(false);

  const activeOrg = orgs.find(o => o.is_active);

  const load = useCallback(async () => {
    try {
      const orgList = await fetchOrganizations();
      setOrgs(orgList);
      const active = orgList.find(o => o.is_active);
      if (active) {
        const m = await fetchOrgMembers(active.id);
        setMembers(m);
      }
    } catch { toast.error("Failed to load team"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleInvite = async () => {
    if (!inviteForm.email.trim()) { toast.error("Enter an email"); return; }
    if (!activeOrg) return;
    setInviteLoading(true);
    try {
      const result = await inviteMember(activeOrg.id, inviteForm);
      toast.success(result?.message || "Invitation created");
      if (result?.email_sent === false && result?.invite_url) {
        try {
          await navigator.clipboard.writeText(result.invite_url);
          toast.success("Invite link copied to clipboard");
        } catch {
          toast.success("Invite created. Share the invite link manually if needed.");
        }
      }
      setShowInvite(false);
      setInviteForm({ email: "", role: "STAFF" });
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to invite"));
    } finally { setInviteLoading(false); }
  };

  const handleRoleChange = async (membershipId, newRole) => {
    if (!activeOrg) return;
    try {
      await updateMemberRole(activeOrg.id, membershipId, { role: newRole });
      toast.success("Role updated");
      await load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to update role")); }
  };

  const handleRemove = async (membershipId, name) => {
    if (!activeOrg || !window.confirm(`Remove ${name} from the team?`)) return;
    try {
      await removeMember(activeOrg.id, membershipId);
      toast.success("Member removed");
      await load();
    } catch (err) { toast.error(getErrorMessage(err, "Failed to remove")); }
  };

  const currentRole = activeOrg?.role;
  const canManage = currentRole === "OWNER" || currentRole === "MANAGER";

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold font-heading text-slate-900">Team</h1>
          <p className="text-sm text-slate-500 mt-0.5">{activeOrg?.name} — {members.length} member{members.length !== 1 ? "s" : ""}</p>
        </div>
        {canManage && (
          <button data-testid="invite-member-btn" onClick={() => setShowInvite(true)}
            className="flex items-center gap-2 h-10 px-4 bg-primary text-primary-foreground rounded-md text-sm font-bold hover:bg-primary/90 transition-colors">
            <UserPlus weight="bold" className="w-4 h-4" /> Invite Member
          </button>
        )}
      </div>

      {showInvite && (
        <div className="bg-white border border-black/10 rounded-md p-5 space-y-4" data-testid="invite-form">
          <h3 className="text-sm font-bold text-slate-900">Invite Team Member</h3>
          <p className="text-xs text-slate-500">Enter their email and UteFlow will send them a secure invite link.</p>
          <div className="flex gap-3">
            <input data-testid="invite-email" type="email" placeholder="colleague@company.com" value={inviteForm.email}
              onChange={e => setInviteForm(p => ({...p, email: e.target.value}))}
              className="flex-1 h-10 px-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-black/10" />
            <select data-testid="invite-role" value={inviteForm.role} onChange={e => setInviteForm(p => ({...p, role: e.target.value}))}
              className="h-10 px-3 text-sm border border-slate-200 rounded-md bg-white">
              <option value="STAFF">Staff</option>
              <option value="MANAGER">Manager</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowInvite(false)} className="h-9 px-4 border border-slate-200 rounded-md text-xs font-bold text-slate-600">Cancel</button>
            <button data-testid="invite-submit" onClick={handleInvite} disabled={inviteLoading}
              className="h-9 px-4 bg-primary text-primary-foreground rounded-md text-xs font-bold disabled:opacity-50">
              {inviteLoading ? "Inviting..." : "Send Invite"}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <div className="min-w-[600px] divide-y divide-slate-100">
        {members.map(m => {
          const RoleIcon = ROLE_ICONS[m.role] || User;
          return (
            <div key={m.membership_id} data-testid={`member-${m.email}`}
              className="flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600">
                  {m.full_name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">{m.full_name}</div>
                  <div className="text-xs text-slate-500">{m.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold ${ROLE_COLORS[m.role]}`}>
                  <RoleIcon weight="bold" className="w-3.5 h-3.5" /> {m.role}
                </span>
                {canManage && m.role !== "OWNER" && m.id !== user?.id && (
                  <div className="flex gap-1">
                    <select data-testid={`role-select-${m.email}`} value={m.role} onChange={e => handleRoleChange(m.membership_id, e.target.value)}
                      className="h-8 px-2 text-xs border border-slate-200 rounded-md bg-white">
                      <option value="STAFF">Staff</option>
                      <option value="MANAGER">Manager</option>
                    </select>
                    <button data-testid={`remove-${m.email}`} onClick={() => handleRemove(m.membership_id, m.full_name)}
                      className="w-8 h-8 rounded-md flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors">
                      <Trash weight="bold" className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
}
